import { computed, Injectable, signal } from '@angular/core';
import { SavedTreeItem, VariableEditorPermissionLevel, VariableType } from '@selfai-platform/pipeline-common';
import { TreeNode } from 'primeng/api';
import { PENDING_NEW_ITEM_ID, PendingNewItem, VariableEditorAction, VariableRowState } from '../models';

@Injectable()
export class VariablesEditingStateService {
  private readonly editingItemId = signal<string | null>(null);
  private readonly pendingNewItem = signal<PendingNewItem | null>(null);
  private readonly selectedNode = signal<TreeNode<SavedTreeItem> | null>(null);
  private readonly permissions = signal<VariableEditorPermissionLevel[]>([]);

  readonly canCreate = computed(() => this.permissions()?.includes(VariableEditorPermissionLevel.CREATE) ?? false);
  readonly canUpdate = computed(
    () =>
      (this.permissions()?.includes(VariableEditorPermissionLevel.UPDATE) ||
        this.permissions()?.includes(VariableEditorPermissionLevel.UPDATE_VALUE_ONLY)) ??
      false,
  );
  readonly canDelete = computed(() => this.permissions()?.includes(VariableEditorPermissionLevel.DELETE) ?? false);
  readonly canRead = computed(() => this.permissions()?.includes(VariableEditorPermissionLevel.READ) ?? false);
  readonly isEditable = computed(() => this.canCreate() || this.canUpdate() || this.canDelete());
  readonly isReadonly = computed(() => this.canRead() && !this.isEditable());
  readonly isEditing = computed(() => this.canUpdate() && !!(this.editingItemId() || this.pendingNewItem()));

  setEditorMode(permissions: VariableEditorPermissionLevel[] | null): void {
    this.permissions.set(permissions ?? []);
  }

  startEditing(itemId: string): void {
    this.editingItemId.set(itemId);
    this.pendingNewItem.set(null);
  }

  startCreatingNew(type: VariableType, parentGroupId?: string): void {
    this.editingItemId.set(PENDING_NEW_ITEM_ID);
    this.pendingNewItem.set({ type, parentGroupId });
  }

  cancelEditing(): void {
    this.editingItemId.set(null);
    this.pendingNewItem.set(null);
  }

  setSelectedNode(node: TreeNode<SavedTreeItem> | null): void {
    this.selectedNode.set(node);
  }

  getEditingItemId(): string | null {
    return this.editingItemId();
  }

  getPendingNewItem(): PendingNewItem | null {
    return this.pendingNewItem();
  }

  getSelectedNode(): TreeNode<SavedTreeItem> | null {
    return this.selectedNode();
  }

  isItemEditing(itemId: string): boolean {
    return this.editingItemId() === itemId;
  }

  getRowState(data: SavedTreeItem): VariableRowState {
    if (this.isEditing()) {
      if (!this.isItemEditing(data.id)) {
        return VariableRowState.READONLY;
      }

      if (this.permissions().includes(VariableEditorPermissionLevel.UPDATE_VALUE_ONLY)) {
        return VariableRowState.EDITING_VALUE_ONLY;
      }

      return VariableRowState.EDITING;
    }

    if (this.isEditable()) {
      return VariableRowState.EDITABLE;
    }

    if (this.isReadonly()) {
      return VariableRowState.READONLY;
    }

    throw new Error('Invalid editor mode');
  }

  isActionEnabled(actionType: VariableEditorAction): boolean {
    if (this.isEditing() || !this.isEditable()) {
      return false;
    }

    if (actionType === 'create') {
      return this.canCreate();
    }

    if (actionType === 'update') {
      return this.canUpdate();
    }

    if (actionType === 'delete') {
      return this.canDelete();
    }

    if (actionType === 'copy-paste') {
      return this.canCreate();
    }

    return true;
  }
}
